import { Link } from 'react-router'
import {
	type ListingEventContext,
	listingLinkHandler,
} from '#app/analytics/listing.ts'
import { RatingMark, ScoreMark } from './score.tsx'

/**
 * The one way a business is presented outside its own page.
 *
 * There were three: a card on the business page showing a favicon, a name, a
 * description and a city badge; separate inline markup on the category hub;
 * and more on the city page. None of them showed a rating, a review count or a
 * score — so a directory whose entire pitch is measurement displayed no
 * measurement anywhere a reader browses.
 *
 * Rendered as a row rather than a card. Rows compare: the eye runs down a
 * column of scores and ratings and can rank them. Cards isolate, which is the
 * wrong affordance for a page whose job is "which of these is best".
 *
 * ## Degrading
 *
 * Score coverage is the exception, not the rule. In production no business is
 * scored yet, while 78% carry a Google rating and 94% carry a description, so
 * this leads with whatever is actually known:
 *
 *   score + rating   → both, score first
 *   rating only      → rating, no empty score slot
 *   neither          → name and description, no numeric furniture at all
 *
 * A placeholder where a score should be advertises an absence. Showing
 * nothing is honest and reads as a listing we have not measured yet.
 */

export interface BusinessListingData {
	id: string
	name: string
	slug: string
	score?: number | null
	ratingValue?: number | null
	ratingCount?: number | null
	bestFor?: string | null
	description?: string | null
	aiDescription?: string | null
	isFeatured?: boolean
	cities?: Array<{ id: string; name: string; slug: string }>
	categories?: Array<{ id: string; name: string; slug: string }>
}

export function BusinessListing({
	business,
	rank,
	href,
	onClick,
	track,
}: {
	business: BusinessListingData
	/** Position in a ranked list. Omitted outside rankings. */
	rank?: number
	/** Overrides the listing URL, for surfaces that track their own links. */
	href?: string
	onClick?: React.MouseEventHandler<HTMLAnchorElement>
	/**
	 * Where this row is being shown. The row reports its own
	 * `listing_result_clicked` from it.
	 *
	 * **Required**, with `false` as the explicit opt-out. Tracking lives in the
	 * component rather than at each call site because the call-site version
	 * silently under-counted: only two discovery surfaces ever wired the
	 * handler, so clicks from search, category hubs and related listings were
	 * invisible. Making this optional would reproduce that exact failure — the
	 * next surface added without it would be silent again, and nothing would
	 * say so. Requiring it turns "I forgot" into a type error.
	 *
	 * Pass `false` only where the surface fires its own event, so nothing
	 * double-counts. `businessId` is filled in from `business`, so a caller
	 * cannot attribute a click to the wrong listing.
	 */
	track: Omit<ListingEventContext, 'businessId'> | false
}) {
	const blurb =
		business.bestFor ?? business.aiDescription ?? business.description
	const place = business.cities?.[0]?.name
	const trade = business.categories?.[0]?.name

	const tracked = track
		? listingLinkHandler('listing_result_clicked', {
				...track,
				businessId: business.id,
				position: track.position ?? rank,
			})
		: undefined

	return (
		<Link
			to={href ?? `/business/${business.slug}`}
			onClick={(event) => {
				tracked?.onClick()
				onClick?.(event)
			}}
			onAuxClick={tracked?.onAuxClick}
			className="group hover:bg-muted/40 flex items-start gap-4 px-1 py-4 transition-colors"
		>
			{business.score != null ? (
				<ScoreMark score={business.score} rank={rank} />
			) : rank != null ? (
				<span className="text-muted-foreground w-8 shrink-0 pt-1 text-center font-mono text-sm tabular-nums">
					{rank}
				</span>
			) : null}

			<div className="min-w-0 flex-1">
				<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
					<span className="font-medium group-hover:underline">
						{business.name}
					</span>
					<RatingMark
						ratingValue={business.ratingValue}
						ratingCount={business.ratingCount}
					/>
					{business.isFeatured ? (
						// Labelled, never implied by styling alone. A placement someone
						// paid for has to say so next to rankings that were earned.
						<span className="text-muted-foreground border-muted-foreground/40 rounded-full border px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase">
							Sponsored
						</span>
					) : null}
				</div>

				{place || trade ? (
					<p className="text-muted-foreground mt-0.5 text-xs">
						{[trade, place].filter(Boolean).join(' · ')}
					</p>
				) : null}

				{blurb ? (
					<p className="text-muted-foreground mt-1.5 line-clamp-2 text-sm leading-relaxed">
						{blurb}
					</p>
				) : null}
			</div>
		</Link>
	)
}

/** Wraps rows so dividers and spacing are consistent wherever they appear. */
export function BusinessListingGroup({
	children,
}: {
	children: React.ReactNode
}) {
	return <ul className="divide-y border-y">{children}</ul>
}
